// Matches frontend-campaign's MAX_VALUE_LENGTH (KEL-387), so a value used here is the same
// value the campaign plugin captures and sends with the order.
const MAX_PARAM_VALUE_LENGTH = 100;

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

// A surrogate pair (e.g. an emoji), or a lone surrogate half
const SURROGATES = /[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDFFF]/g;
const TRAILING_HIGH_SURROGATE = /[\uD800-\uDBFF]$/;

// encodeURIComponent throws on a lone surrogate half, so each one becomes U+FFFD, as
// URLSearchParams does when it decodes a url
const toWellFormed = value =>
  value.replace(SURROGATES, match => (match.length === 2 ? match : '\uFFFD'));

/**
 * Cleans a query parameter value with frontend-campaign's rule (see its sanitiseValue in
 * src/helpers/params.js): control characters removed, trimmed and capped at
 * MAX_PARAM_VALUE_LENGTH. A character the cap cuts in half (e.g. an emoji) is dropped, so the
 * value can always be encoded. Returns null when nothing is left.
 */
const sanitiseParamValue = value => {
  if (typeof value !== 'string') return null;
  const capped = value
    .replace(CONTROL_CHARS, '')
    .trim()
    .slice(0, MAX_PARAM_VALUE_LENGTH)
    .replace(TRAILING_HIGH_SURROGATE, '');
  const cleaned = toWellFormed(capped);
  return cleaned || null;
};

const splitUrl = (url = '') => {
  const hashIndex = url.indexOf('#');
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const queryIndex = beforeHash.indexOf('?');
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
  const search = queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1);
  return { path, search, hash };
};

/**
 * Reads a query parameter from a url or Next.js asPath, ignoring the hash. Returns null when
 * the url doesn't have it.
 */
const getQueryParam = (url, name) => {
  if (typeof url !== 'string') return null;
  return new URLSearchParams(splitUrl(url).search).get(name);
};

/**
 * Adds name=value to a relative or absolute url, before any fragment, e.g.
 * /match-magazine#product-variants becomes /match-magazine?offer=X#product-variants. The
 * existing query string is kept as it is and both name and value are encoded. A url that
 * already has the parameter is returned unchanged, so its own value wins.
 */
const setQueryParam = (url, name, value) => {
  if (typeof url !== 'string' || !name || !value) return url;
  const { path, search, hash } = splitUrl(url);
  if (new URLSearchParams(search).has(name)) return url;
  const param = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  return `${path}?${search ? `${search}&` : ''}${param}${hash}`;
};

export { MAX_PARAM_VALUE_LENGTH, sanitiseParamValue, getQueryParam, setQueryParam };
